import { useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useFetchye } from 'fetchye';
import { addLogToReport, addErrorToReport } from '@americanexpress/one-app-ducks';

export const READ_CUSTOMER_APPLICATION_URL = '/ReadCustomerApplicationsList.v1';
export const UPDATE_CUSTOMER_APPLICATION_URL = '/UpdateCustomerApplication.v1';
export const READ_ELETTER_URL = '/ReadCustomerApplicationDecisionEletter.v1';
export const CONFIRM_ESIGN_DISAGREEMENT_URL = '/ConfirmESignDisagreement.v1';
export const DEFAULT_MARKET_CODE = 'US';
const JWT_SEGMENT_REGEX = /^[\w-]+$/;

export const buildSearchStatusPayload = ({
  ssn,
  postcode,
  marketCode = DEFAULT_MARKET_CODE,
}) => ({
  ssn,
  zipcode: postcode,
  market_cd: marketCode,
});

export const buildCorrelationId = () => {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export const isLikelyJwtToken = (token = '') => {
  if (typeof token !== 'string') return false;

  const segments = token.split('.');
  return (
    segments.length === 3
    && segments.every((segment) => segment && JWT_SEGMENT_REGEX.test(segment))
  );
};

/**
 * Decodes the payload of a JWT token and returns the parsed JSON object.
 * Returns null if the token is invalid or the payload cannot be decoded.
 */
export const decodeJwtPayload = (token) => {
  if (!isLikelyJwtToken(token)) return null;

  try {
    const payloadBase64Url = token.split('.')[1];
    // Convert base64url encoding to standard base64
    const base64 = payloadBase64Url.replace(/-/g, '+').replace(/_/g, '/');
    // Pad to a multiple of 4 characters
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
};

export const buildAuthorizationHeader = (userSessionToken) => {
  if (!isLikelyJwtToken(userSessionToken)) {
    return {};
  }

  return {
    Authorization: `Bearer ${userSessionToken}`,
  };
};

export const buildSearchStatusRequestOptions = (payload, userSessionToken = null) => ({
  defer: true,
  method: 'POST',
  credentials: 'include',
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'one-data-correlation-id': buildCorrelationId(),
    ...buildAuthorizationHeader(userSessionToken),
  },
  body: JSON.stringify(payload),
});

export const extractUpdateCustomerApplicationToken = (responseData) =>
  responseData?.body?.token || responseData?.token || null;

export const buildReadELetterRequestOptions = (token) => ({
  defer: true,
  method: 'GET',
  credentials: 'include',
  headers: {
    Authorization: `Bearer ${token}`,
  },
});

export const useSearchStatusRequest = (formValues) => {
  const dispatch = useDispatch();
  const apiBaseUrl = useSelector((state) =>
    state.getIn(['config', 'oneDataApiUrl']),
  );
  const userSessionToken = useSelector((state) =>
    state.getIn(['modules', 'unify-status-site', 'general', 'userSession']),
  );
  const endpoint = useMemo(() => {
    if (!apiBaseUrl) {
      return null;
    }
    return `${apiBaseUrl}${READ_CUSTOMER_APPLICATION_URL}`;
  }, [apiBaseUrl]);

  const payload = useMemo(
    () => buildSearchStatusPayload(formValues),
    [formValues],
  );

  const { run, isLoading, data, error } = useFetchye(
    endpoint,
    buildSearchStatusRequestOptions(payload, userSessionToken),
  );

  const runSearchStatusRequest = useCallback(async () => {
    dispatch(addLogToReport('info', 'ReadCustomerApplicationsList request initiated', {
      moduleID: 'unify-status-site',
      api: 'ReadCustomerApplicationsList',
      endpoint: READ_CUSTOMER_APPLICATION_URL,
    }));
    try {
      const result = await run();
      if (result?.error) {
        dispatch(addErrorToReport(
          result.error instanceof Error ? result.error : new Error('ReadCustomerApplicationsList request failed'),
          { moduleID: 'unify-status-site', api: 'ReadCustomerApplicationsList', endpoint: READ_CUSTOMER_APPLICATION_URL }
        ));
      } else {
        dispatch(addLogToReport('info', 'ReadCustomerApplicationsList request succeeded', {
          moduleID: 'unify-status-site',
          api: 'ReadCustomerApplicationsList',
          endpoint: READ_CUSTOMER_APPLICATION_URL,
          status: 'success',
        }));
      }
      return result;
    } catch (err) {
      dispatch(addErrorToReport(
        err instanceof Error ? err : new Error(String(err)),
        { moduleID: 'unify-status-site', api: 'ReadCustomerApplicationsList', endpoint: READ_CUSTOMER_APPLICATION_URL }
      ));
      throw err;
    }
  }, [run, dispatch]);

  return {
    runSearchStatusRequest,
    isLoading,
    data,
    error,
  };
};

export const buildEdocumentViewedDatetime = () => {
  const now = new Date();
  const pad = (n, len = 2) => String(n).padStart(len, '0');
  const date =
    `${now.getUTCFullYear()}${pad(now.getUTCMonth() + 1)}${pad(now.getUTCDate())}`;
  const time =
    `${pad(now.getUTCHours())}${pad(now.getUTCMinutes())}${pad(now.getUTCSeconds())}` +
    `.${String(now.getUTCMilliseconds()).padStart(3, '0')}`;
  return `${date}T${time} GMT`;
};

export const buildUpdateCustomerApplicationPayload = ({ pcn, actionCode, event }) => {
  if (!pcn) throw new Error('buildUpdateCustomerApplicationPayload: pcn is required');
  if (!actionCode) throw new Error('buildUpdateCustomerApplicationPayload: actionCode is required');
  if (!event) throw new Error('buildUpdateCustomerApplicationPayload: event is required');

  return {
    pcn_hash: pcn,
    action_code: actionCode,
    event,
    event_time_stamp: buildEdocumentViewedDatetime(),
  };
};

export const useUpdateCustomerApplicationRequest = (pcn, actionCode, event) => {
  const dispatch = useDispatch();
  const apiBaseUrl = useSelector((state) =>
    state.getIn(['config', 'oneDataApiUrl']),
  );
  const userSessionToken = useSelector((state) =>
    state.getIn(['modules', 'unify-status-site', 'general', 'userSession']),
  );
  const endpoint = useMemo(() => {
    if (!apiBaseUrl || !pcn) {
      return null;
    }
    return `${apiBaseUrl}${UPDATE_CUSTOMER_APPLICATION_URL}`;
  }, [apiBaseUrl, pcn]);

  const payload = useMemo(
    () =>
      pcn ? buildUpdateCustomerApplicationPayload({ pcn, actionCode, event }) : {},
    [pcn, actionCode, event],
  );

  const { run, isLoading, data, error } = useFetchye(
    endpoint,
    buildSearchStatusRequestOptions(payload, userSessionToken),
  );

  const runUpdateCustomerApplicationRequest = useCallback(async () => {
    dispatch(addLogToReport('info', 'UpdateCustomerApplication request initiated', {
      moduleID: 'unify-status-site',
      api: 'UpdateCustomerApplication',
      endpoint: UPDATE_CUSTOMER_APPLICATION_URL,
    }));
    try {
      const result = await run();
      if (result?.error) {
        dispatch(addErrorToReport(
          result.error instanceof Error ? result.error : new Error('UpdateCustomerApplication request failed'),
          { moduleID: 'unify-status-site', api: 'UpdateCustomerApplication', endpoint: UPDATE_CUSTOMER_APPLICATION_URL }
        ));
      } else {
        dispatch(addLogToReport('info', 'UpdateCustomerApplication request succeeded', {
          moduleID: 'unify-status-site',
          api: 'UpdateCustomerApplication',
          endpoint: UPDATE_CUSTOMER_APPLICATION_URL,
          status: 'success',
        }));
      }
      return result;
    } catch (err) {
      dispatch(addErrorToReport(
        err instanceof Error ? err : new Error(String(err)),
        { moduleID: 'unify-status-site', api: 'UpdateCustomerApplication', endpoint: UPDATE_CUSTOMER_APPLICATION_URL }
      ));
      throw err;
    }
  }, [run, dispatch]);

  return {
    runUpdateCustomerApplicationRequest,
    isLoading,
    data,
    error,
  };
};

export const buildVerifyBirthdatePayload = ({ pcn, birthdate }) => ({
  ...buildUpdateCustomerApplicationPayload({
    pcn,
    actionCode: 'ACCEPTED',
    event: 'ELECTRONIC_COMMUNICATION_CONSENT',
  }),
  dob: birthdate,
});

export const useVerifyBirthdateRequest = (pcn, birthdate) => {
  const dispatch = useDispatch();
  const apiBaseUrl = useSelector((state) =>
    state.getIn(['config', 'oneDataApiUrl']),
  );
  const userSessionToken = useSelector((state) =>
    state.getIn(['modules', 'unify-status-site', 'general', 'userSession']),
  );
  const endpoint = useMemo(() => {
    if (!apiBaseUrl || !pcn) {
      return null;
    }
    return `${apiBaseUrl}${UPDATE_CUSTOMER_APPLICATION_URL}`;
  }, [apiBaseUrl, pcn]);

  const { run, isLoading, data, error } = useFetchye(
    endpoint,
    buildSearchStatusRequestOptions(
      pcn ? buildVerifyBirthdatePayload({ pcn, birthdate }) : {},
      userSessionToken,
    ),
  );

  const runVerifyBirthdateRequest = useCallback(async () => {
    dispatch(addLogToReport('info', 'VerifyBirthdate request initiated', {
      moduleID: 'unify-status-site',
      api: 'VerifyBirthdate',
      endpoint: UPDATE_CUSTOMER_APPLICATION_URL,
    }));
    try {
      const result = await run();
      if (result?.error) {
        dispatch(addErrorToReport(
          result.error instanceof Error ? result.error : new Error('VerifyBirthdate request failed'),
          { moduleID: 'unify-status-site', api: 'VerifyBirthdate', endpoint: UPDATE_CUSTOMER_APPLICATION_URL }
        ));
      } else {
        dispatch(addLogToReport('info', 'VerifyBirthdate request succeeded', {
          moduleID: 'unify-status-site',
          api: 'VerifyBirthdate',
          endpoint: UPDATE_CUSTOMER_APPLICATION_URL,
          status: 'success',
        }));
      }
      return result;
    } catch (err) {
      dispatch(addErrorToReport(
        err instanceof Error ? err : new Error(String(err)),
        { moduleID: 'unify-status-site', api: 'VerifyBirthdate', endpoint: UPDATE_CUSTOMER_APPLICATION_URL }
      ));
      throw err;
    }
  }, [run, dispatch]);

  return {
    runVerifyBirthdateRequest,
    isLoading,
    data,
    error,
  };
};

export const buildConfirmESignDisagreementPayload = ({ pcn }) =>
  buildUpdateCustomerApplicationPayload({
    pcn,
    actionCode: 'DECLINED',
    event: 'ELECTRONIC_COMMUNICATION_CONSENT',
  });

export const useConfirmESignDisagreementRequest = (pcn) => {
  const dispatch = useDispatch();
  const apiBaseUrl = useSelector((state) =>
    state.getIn(['config', 'oneDataApiUrl']),
  );
  const userSessionToken = useSelector((state) =>
    state.getIn(['modules', 'unify-status-site', 'general', 'userSession']),
  );
  const endpoint = useMemo(() => {
    if (!apiBaseUrl || !pcn) {
      return null;
    }
    return `${apiBaseUrl}${UPDATE_CUSTOMER_APPLICATION_URL}`;
  }, [apiBaseUrl, pcn]);

  const { run, isLoading, data, error } = useFetchye(
    endpoint,
    buildSearchStatusRequestOptions(
      pcn ? buildConfirmESignDisagreementPayload({ pcn }) : {},
      userSessionToken,
    ),
  );

  const runConfirmESignDisagreementRequest = useCallback(async () => {
    dispatch(addLogToReport('info', 'ConfirmESignDisagreement request initiated', {
      moduleID: 'unify-status-site',
      api: 'ConfirmESignDisagreement',
      endpoint: UPDATE_CUSTOMER_APPLICATION_URL,
    }));
    try {
      const result = await run();
      if (result?.error) {
        dispatch(addErrorToReport(
          result.error instanceof Error ? result.error : new Error('ConfirmESignDisagreement request failed'),
          { moduleID: 'unify-status-site', api: 'ConfirmESignDisagreement', endpoint: UPDATE_CUSTOMER_APPLICATION_URL }
        ));
      } else {
        dispatch(addLogToReport('info', 'ConfirmESignDisagreement request succeeded', {
          moduleID: 'unify-status-site',
          api: 'ConfirmESignDisagreement',
          endpoint: UPDATE_CUSTOMER_APPLICATION_URL,
          status: 'success',
        }));
      }
      return result;
    } catch (err) {
      dispatch(addErrorToReport(
        err instanceof Error ? err : new Error(String(err)),
        { moduleID: 'unify-status-site', api: 'ConfirmESignDisagreement', endpoint: UPDATE_CUSTOMER_APPLICATION_URL }
      ));
      throw err;
    }
  }, [run, dispatch]);

  return {
    runConfirmESignDisagreementRequest,
    isLoading,
    data,
    error,
  };
};

export const useReadELetterRequest = (token) => {
  const dispatch = useDispatch();
  const apiBaseUrl = useSelector((state) =>
    state.getIn(['config', 'oneDataApiUrl']),
  );
  const endpoint = useMemo(() => {
    if (!apiBaseUrl || !token) {
      return null;
    }
    return `${apiBaseUrl}${READ_ELETTER_URL}`;
  }, [apiBaseUrl, token]);

  const { run, isLoading, data, error } = useFetchye(
    endpoint,
    buildReadELetterRequestOptions(token),
  );

  const runReadELetterRequest = useCallback(async () => {
    dispatch(addLogToReport('info', 'ReadCustomerApplicationDecisionEletter request initiated', {
      moduleID: 'unify-status-site',
      api: 'ReadCustomerApplicationDecisionEletter',
      endpoint: READ_ELETTER_URL,
    }));
    try {
      const result = await run();
      if (result?.error) {
        dispatch(addErrorToReport(
          result.error instanceof Error ? result.error : new Error('ReadCustomerApplicationDecisionEletter request failed'),
          { moduleID: 'unify-status-site', api: 'ReadCustomerApplicationDecisionEletter', endpoint: READ_ELETTER_URL }
        ));
      } else {
        dispatch(addLogToReport('info', 'ReadCustomerApplicationDecisionEletter request succeeded', {
          moduleID: 'unify-status-site',
          api: 'ReadCustomerApplicationDecisionEletter',
          endpoint: READ_ELETTER_URL,
          status: 'success',
        }));
      }
      return result;
    } catch (err) {
      dispatch(addErrorToReport(
        err instanceof Error ? err : new Error(String(err)),
        { moduleID: 'unify-status-site', api: 'ReadCustomerApplicationDecisionEletter', endpoint: READ_ELETTER_URL }
      ));
      throw err;
    }
  }, [run, dispatch]);

  return {
    runReadELetterRequest,
    isLoading,
    data,
    error,
  };
};
